import React from 'react';
import { Device } from '../../types';
import { PendingDevice } from '../../types/api';
import { GatewayRecord } from '../../services/gatewaysApi';

function guessDeviceType(device: any): string {
    const model = (device.model_id || '').toLowerCase();
    const desc = (device.description || '').toLowerCase();
    if (model.includes('iomzb') || desc.includes('io module') || desc.includes('relay module')) return 'io_module';
    if (desc.includes('motion') || desc.includes('occupancy') || desc.includes('presence')) return 'motion';
    if (desc.includes('contact') || desc.includes('door') || desc.includes('window') || desc.includes('magnet')) return 'contact';
    if (desc.includes('power meter') || desc.includes('energy meter') || desc.includes('smart plug') || desc.includes('metering')) return 'power_meter';
    if (desc.includes('thermostat')) return 'thermostat';
    if (desc.includes('dimmer')) return 'dimmer';
    if (desc.includes('switch') || desc.includes('plug') || desc.includes('outlet') || desc.includes('socket')) return 'switch';
    if (desc.includes('bulb') || desc.includes('light') || desc.includes('lamp')) return 'light';
    if (desc.includes('humidity') || desc.includes('temperature')) return 'temperature';
    return 'temperature';
}

interface Props {
    pairingMode: boolean;
    pairingCountdown: number;
    coordinatorConnected: boolean;
    zigbeeGateways: GatewayRecord[];
    pairingCoordinatorId: string;
    pendingDevices: PendingDevice[];
    confirmForms: Record<string, { name: string; type: string }>;
    replaceMode: Record<string, boolean>;
    replaceTarget: Record<string, string>;
    devices: Device[];
    onPairingCoordinatorChange: (id: string) => void;
    onEnablePairing: () => void;
    onStopPairing: () => void;
    onConfirmDevice: (ieee: string) => void;
    onDismissPending: (ieee: string) => void;
    onReplaceDevice: (ieee: string) => void;
    onConfirmFormChange: (ieee: string, field: 'name' | 'type', value: string) => void;
    onToggleReplaceMode: (ieee: string, on: boolean) => void;
    onReplaceTargetChange: (ieee: string, value: string) => void;
}

const PairTab: React.FC<Props> = ({
    pairingMode, pairingCountdown, coordinatorConnected, zigbeeGateways, pairingCoordinatorId,
    pendingDevices, confirmForms, replaceMode, replaceTarget, devices,
    onPairingCoordinatorChange, onEnablePairing, onStopPairing, onConfirmDevice,
    onDismissPending, onReplaceDevice, onConfirmFormChange, onToggleReplaceMode, onReplaceTargetChange,
}) => (
    <>
        <div style={{ paddingTop: '16px' }}>
            {/* Pair controls */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '20px' }}>
                {zigbeeGateways.length > 1 && (
                    <select
                        value={pairingCoordinatorId}
                        onChange={e => onPairingCoordinatorChange(e.target.value)}
                        disabled={pairingMode}
                        style={{ padding: '10px 12px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', cursor: pairingMode ? 'not-allowed' : 'pointer', flex: '1 1 200px' }}
                        title="Välj vilken samordnare du vill parkoppla mot"
                    >
                        <option value="">📡 Alla samordnare</option>
                        {zigbeeGateways.map(gw => (
                            <option key={gw.id} value={gw.id}>📡 {gw.name}</option>
                        ))}
                    </select>
                )}
                <button
                    onClick={onEnablePairing}
                    disabled={pairingMode || !coordinatorConnected}
                    title={!coordinatorConnected ? 'Ingen Zigbee-samordnare ansluten' : undefined}
                    style={{
                        padding: '12px 20px',
                        backgroundColor: pairingMode ? '#6c757d' : !coordinatorConnected ? '#adb5bd' : '#007bff',
                        color: 'white', border: 'none', borderRadius: '4px',
                        cursor: (pairingMode || !coordinatorConnected) ? 'not-allowed' : 'pointer',
                        fontSize: '15px', fontWeight: 600,
                        opacity: (pairingMode || !coordinatorConnected) ? 0.6 : 1,
                        flex: '1 1 200px',
                    }}
                >
                    {pairingMode ? `⏱ Parkopplar… ${pairingCountdown} s` : '🔌 Parkoppla ny enhet'}
                </button>
                {pairingMode && (
                    <button
                        onClick={onStopPairing}
                        style={{ padding: '12px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '15px', fontWeight: 600, flex: '1 1 140px' }}
                    >
                        ⛔ Stoppa parkoppling
                    </button>
                )}
            </div>

            {/* Pairing Mode Banner */}
            {pairingMode && (
                <div style={{
                    backgroundColor: '#d1ecf1',
                    color: '#0c5460',
                    padding: '15px',
                    marginBottom: '20px',
                    borderRadius: '4px',
                    border: '1px solid #bee5eb'
                }}>
                    <div style={{ marginBottom: '10px' }}>
                        <strong>🔵 Parkopplingsläget är på</strong> – tid kvar: <strong>{pairingCountdown} s</strong>
                    </div>
                    <div style={{ fontSize: '14px' }}>
                        📱 Sätt din Zigbee-enhet i parkopplingsläge nu (oftast genom att hålla in parkopplingsknappen i 5–10 sekunder).
                        Enheter som ansluter dyker upp här nedanför så att du kan bekräfta dem.
                    </div>
                </div>
            )}

            {!pairingMode && pendingDevices.length === 0 && !coordinatorConnected && (
                <p style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                    Ingen Zigbee-samordnare ansluten. Kontrollera din gateway under fliken Gateways.
                </p>
            )}
            {!pairingMode && pendingDevices.length === 0 && coordinatorConnected && (
                <p style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    Klicka på ”Parkoppla ny enhet” ovanför för att börja.
                </p>
            )}
        </div>

        {/* Pending Devices Section */}
        {pendingDevices.length > 0 && (
            <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                padding: '20px',
                marginBottom: '20px'
            }}>
                <h3 style={{ marginTop: 0 }}>⏳ Enheter som väntar på bekräftelse ({pendingDevices.length})</h3>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                    De här enheterna har anslutit sig till Zigbee-nätet. Ge varje enhet ett namn och en typ för att registrera den.
                </p>
                {pendingDevices.map(device => (
                    <div key={device.ieee_address} style={{
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '15px',
                        marginBottom: '10px'
                    }}>
                        <div style={{ marginBottom: '10px' }}>
                            <code style={{ fontSize: '13px', color: '#555' }}>{device.ieee_address}</code>
                            {device.description && <span style={{ marginLeft: '10px', color: '#888', fontSize: '13px' }}>{device.vendor} – {device.description}</span>}
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            {!replaceMode[device.ieee_address] ? (
                                <>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', marginBottom: '4px' }}>Namn</label>
                                        <input
                                            type="text"
                                            placeholder="t.ex. Givare vardagsrum"
                                            value={confirmForms[device.ieee_address]?.name || ''}
                                            onChange={e => onConfirmFormChange(device.ieee_address, 'name', e.target.value)}
                                            style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', width: '200px' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', marginBottom: '4px' }}>Typ</label>
                                        <select
                                            value={confirmForms[device.ieee_address]?.type || guessDeviceType(device)}
                                            onChange={e => onConfirmFormChange(device.ieee_address, 'type', e.target.value)}
                                            style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                                        >
                                            <option value="temperature">Temperaturgivare</option>
                                            <option value="humidity">Fuktgivare</option>
                                            <option value="motion">Rörelsevakt</option>
                                            <option value="contact">Dörr-/fönstergivare</option>
                                            <option value="light">Lampa</option>
                                            <option value="switch">Strömbrytare/uttag</option>
                                            <option value="dimmer">Dimmer</option>
                                            <option value="thermostat">Termostat</option>
                                            <option value="power_meter">Elmätare</option>
                                            <option value="io_module">IO-modul</option>
                                        </select>
                                    </div>
                                    <button
                                        onClick={() => onConfirmDevice(device.ieee_address)}
                                        style={{ padding: '6px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                                    >
                                        ✓ Bekräfta
                                    </button>
                                    <button
                                        onClick={() => onToggleReplaceMode(device.ieee_address, true)}
                                        style={{ padding: '6px 16px', backgroundColor: '#fd7e14', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                        title="Ersätt en befintlig enhet — all historik följer med"
                                    >
                                        ↔ Ersätt befintlig
                                    </button>
                                    <button
                                        onClick={() => onDismissPending(device.ieee_address)}
                                        style={{ padding: '6px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                    >
                                        ✕ Avvisa
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', marginBottom: '4px' }}>Vilken enhet ska ersättas?</label>
                                        <select
                                            value={replaceTarget[device.ieee_address] || ''}
                                            onChange={e => onReplaceTargetChange(device.ieee_address, e.target.value)}
                                            style={{ padding: '6px 10px', border: '1px solid #fd7e14', borderRadius: '4px', fontSize: '14px', minWidth: '220px' }}
                                        >
                                            <option value="">— välj enhet —</option>
                                            {devices.map(d => (
                                                <option key={d.id} value={d.id}>{d.name} ({d.id})</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#856404', alignSelf: 'flex-end', paddingBottom: '8px' }}>
                                        Historiken sparas och hamnar under den valda enheten.
                                    </div>
                                    <button
                                        onClick={() => onReplaceDevice(device.ieee_address)}
                                        style={{ padding: '6px 16px', backgroundColor: '#fd7e14', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                                    >
                                        ↔ Ersätt
                                    </button>
                                    <button
                                        onClick={() => onToggleReplaceMode(device.ieee_address, false)}
                                        style={{ padding: '6px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                    >
                                        ← Tillbaka
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        )}
    </>
);

export default PairTab;
